import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "../styles/Navbar.css";

import { BRAND_NAME, BRAND_NAV } from "../data/contactInfo";
import BrandLogo, { BRAND_LOGO_SRC } from "./BrandLogo";

const logo = BRAND_LOGO_SRC;

const topLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/chenda-melam-chennai", label: "Weddings" },
];

const dropdowns = [
  {
    id: "learn",
    label: "Learn",
    items: [
      { to: "/chenda-classes-chennai", label: "Classes", note: "Online & offline" },
      { to: "/courses", label: "Courses", note: "Full training programs" },
    ],
  },
  {
    id: "explore",
    label: "Explore",
    items: [
      { to: "/melams", label: "Melams" },
      { to: "/fusions", label: "Fusions", note: "Melam + other instruments" },
      { to: "/instruments", label: "Instruments" },
      { to: "/programs", label: "Programs" },
      { to: "/gallery", label: "Gallery" },
      { to: "/testimonials", label: "Testimonials" },
    ],
  },
];

const endLinks = [{ to: "/contact", label: "Contact" }];

const isPathInItems = (pathname, items) =>
  items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDesktop, setOpenDesktop] = useState(null);
  const [openMobile, setOpenMobile] = useState(null);
  const desktopNavRef = useRef(null);
  const closeTimerRef = useRef(null);
  const location = useLocation();

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openDropdown = (id) => {
    clearCloseTimer();
    setOpenDesktop(id);
  };

  const scheduleCloseDropdown = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpenDesktop(null);
      closeTimerRef.current = null;
    }, 160);
  };

  useEffect(() => {
    setMenuOpen(false);
    setOpenDesktop(null);
    setOpenMobile(null);
    clearCloseTimer();
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(e.target)) {
        clearCloseTimer();
        setOpenDesktop(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        clearCloseTimer();
        setOpenDesktop(null);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      clearCloseTimer();
    };
  }, []);

  const renderNavLink = (link, className) => (
    <NavLink
      key={link.to}
      to={link.to}
      className={({ isActive }) => `${className}${isActive ? " active" : ""}`}
      end={link.end || link.to === "/"}
      onClick={() => setMenuOpen(false)}
    >
      {link.label}
    </NavLink>
  );

  return (
    <header className="navbar-wrapper">
      <nav className="navbar" aria-label="Main navigation">
        <div className="navbar-inner">
          <Link to="/" className="navbar-brand" onClick={() => setMenuOpen(false)}>
            <img src={logo} alt={BRAND_NAME} className="logo-img" />
            <div className="brand-text">
              <span className="brand-name">{BRAND_NAV.line1}</span>
              <span className="brand-sub">{BRAND_NAV.line2}</span>
            </div>
          </Link>

          <div className="navbar-center" ref={desktopNavRef}>
            <div className="navbar-links">
              {topLinks.map((link) => renderNavLink(link, "navbar-link"))}

              {dropdowns.map((group) => {
                const isOpen = openDesktop === group.id;
                const isActive = isPathInItems(location.pathname, group.items);

                return (
                  <div
                    key={group.id}
                    className={`nav-dropdown${isOpen ? " open" : ""}${isActive ? " active-group" : ""}`}
                    onMouseEnter={() => openDropdown(group.id)}
                    onMouseLeave={scheduleCloseDropdown}
                  >
                    <button
                      type="button"
                      className={`nav-dropdown-trigger${isActive ? " active" : ""}`}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      onClick={() =>
                        setOpenDesktop((prev) => (prev === group.id ? null : group.id))
                      }
                    >
                      {group.label}
                      <span className="nav-caret" aria-hidden="true" />
                    </button>

                    <div className="nav-dropdown-menu" role="menu">
                      <div className="nav-dropdown-menu-inner">
                        {group.items.map((item) => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            role="menuitem"
                            className={({ isActive }) =>
                              `nav-dropdown-item${isActive ? " active" : ""}`
                            }
                            onClick={() => {
                              clearCloseTimer();
                              setOpenDesktop(null);
                            }}
                          >
                            <span className="nav-dropdown-item-label">{item.label}</span>
                            {item.note ? (
                              <span className="nav-dropdown-item-note">{item.note}</span>
                            ) : null}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {endLinks.map((link) => renderNavLink(link, "navbar-link"))}
            </div>
          </div>

          <div className="navbar-end">
            <Link to={{ pathname: "/contact", search: "?type=wedding&form=open" }} className="navbar-cta">
              Book Melam
            </Link>
            <button
              type="button"
              className={`navbar-toggle${menuOpen ? " open" : ""}`}
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`navbar-overlay${menuOpen ? " visible" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <aside className={`navbar-mobile${menuOpen ? " open" : ""}`} aria-hidden={!menuOpen}>
        <div className="mobile-brand">
          <BrandLogo
            size="md"
            linked
            onClick={() => setMenuOpen(false)}
            showName
            name={BRAND_NAV.line1}
            tagline={BRAND_NAV.line2}
          />
        </div>

        <div className="mobile-links">
          {topLinks.map((link) => renderNavLink(link, "mobile-link"))}

          {dropdowns.map((group) => {
            const isOpen = openMobile === group.id;
            const isActive = isPathInItems(location.pathname, group.items);

            return (
              <div
                key={group.id}
                className={`mobile-accordion${isOpen ? " open" : ""}${isActive ? " active-group" : ""}`}
              >
                <button
                  type="button"
                  className={`mobile-accordion-trigger${isActive ? " active" : ""}`}
                  aria-expanded={isOpen}
                  onClick={() =>
                    setOpenMobile((prev) => (prev === group.id ? null : group.id))
                  }
                >
                  {group.label}
                  <span className="nav-caret" aria-hidden="true" />
                </button>

                <div className="mobile-accordion-panel" hidden={!isOpen}>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `mobile-sublink${isActive ? " active" : ""}`
                      }
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}

          {endLinks.map((link) => renderNavLink(link, "mobile-link"))}
        </div>

        <Link
          to={{ pathname: "/contact", search: "?type=wedding&form=open" }}
          className="mobile-cta"
          onClick={() => setMenuOpen(false)}
        >
          Book Melam
        </Link>
      </aside>
    </header>
  );
};

export default Navbar;
